import { DATE_TIME_FORMAT, ROUTES } from "@lhd/constants";
import {
  getWorkerLogtimeApiErrorMessage,
  hasWorkerLogtimeEditPermission,
  hasWorkerLogtimeViewPermission,
  useFetchWorkerLogTimes,
  useFetchWorkerLogTimesByDate,
  useFetchWorkerLogtimePermissions,
  useSaveWorkerLogtimesSave,
} from "@lhd/hooks";
import {
  CrossCompanyOverlapError,
  validateCrossCompanyOverlapBeforeSave,
} from "@lhd/features/TrackingTime/crossCompanyOverlap";
import {
  createVirtualLogtimesBeforeSave,
  remapFormKeysAfterCreate,
  syncFormAttachmentsAfterWorkerLogtimeCreate,
  TimelineValidationError,
  validateTrackingTimelineForm,
} from "./createVirtualLogtimesBeforeSave";
import { getCompanyId, setCompanyId } from "@lhd/utility";
import { user } from "@lhd/features/users/selector";
import { Form, notification } from "antd";
import dayjs from "dayjs";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  buildWorkerLogtimesSaveFormData,
  buildWorkerLogtimesSaveJsonPayload,
  hasWorkerLogtimesFormChanges,
  remapFormKeysAfterJsonSave,
} from "./buildSaveFormData";
import { useAttendanceManagementPermission } from "../useAttendanceManagementPermission";
import { useManagerTrackingPath } from "../useManagerTrackingPath";
import { useServerNowMs } from "../useServerNowMs";
import { hasOpenWorkSession } from "../HomeTracking/until";
import {
  isOpenSessionAfterPunch,
  subscribeWorkerLogtimePunch,
} from "../workerLogtimePunchSync";
import {
  isPastWorkDate,
  isTodayWorkDate,
  nowInTokyo,
} from "../workLogTimezone";

export type ReceivedProps = Record<string, any>;

export const parseWorkerId = (raw: string | null): number | undefined => {
  if (raw == null || raw === "") return undefined;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : undefined;
};

const toWorkDateApi = (currentDate: string): string => {
  const normalized = currentDate.trim().replace(/\//g, "-");
  const d = dayjs(normalized);
  return d.isValid() ? d.format("YYYY-MM-DD") : normalized;
};

const normalizeWorkDate = (value?: string | null) => {
  const raw = (value ?? "").trim();
  if (!raw) return dayjs().format(DATE_TIME_FORMAT.DATE_DEFAULT);
  const d = dayjs(raw.replace(/-/g, "/"));
  return d.isValid() ? d.format(DATE_TIME_FORMAT.DATE_DEFAULT) : raw;
};

const msUntilNextMinuteTokyo = (): number => {
  const now = nowInTokyo();
  const nextMinute = now.startOf("minute").add(1, "minute");
  return Math.max(0, nextMinute.diff(now));
};

const useDetailTracking = (props: ReceivedProps) => {
  const navigate = useNavigate();
  const auth = useSelector(user);
  const { listPath } = useManagerTrackingPath();
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    canManageWorkersAttendance,
    isSuperAdmin,
    currentWorkerId,
    companies,
  } = useAttendanceManagementPermission();
  const companyId = getCompanyId();

  const workerId = useMemo(
    () => parseWorkerId(searchParams.get("worker_id")),
    [searchParams],
  );

  const shouldFetchLogtimePermissions =
    Boolean(auth) && !canManageWorkersAttendance;

  const { data: logtimePermissions, isLoading: isLoadingLogtimePermissions } =
    useFetchWorkerLogtimePermissions(shouldFetchLogtimePermissions);

  const hasDelegatedViewPermission = useMemo(
    () => hasWorkerLogtimeViewPermission(logtimePermissions, workerId),
    [logtimePermissions, workerId],
  );

  const hasDelegatedEditPermission = useMemo(
    () => hasWorkerLogtimeEditPermission(logtimePermissions, workerId),
    [logtimePermissions, workerId],
  );

  const canViewWorker = useMemo(() => {
    if (canManageWorkersAttendance) return true;
    if (currentWorkerId != null && workerId === currentWorkerId) return true;
    return hasDelegatedViewPermission;
  }, [
    canManageWorkersAttendance,
    currentWorkerId,
    hasDelegatedViewPermission,
    workerId,
  ]);

  const isDelegatedPermissionsReady = useMemo(
    () =>
      shouldFetchLogtimePermissions &&
      !isLoadingLogtimePermissions &&
      logtimePermissions != null,
    [
      shouldFetchLogtimePermissions,
      isLoadingLogtimePermissions,
      logtimePermissions,
    ],
  );

  const shouldRedirectUnauthorizedViewer = useMemo(
    () => isDelegatedPermissionsReady && !canViewWorker,
    [canViewWorker, isDelegatedPermissionsReady],
  );

  const wantsEditMode = searchParams.get("mode") === "edit";

  const canEditAllAttendanceFields =
    canManageWorkersAttendance || hasDelegatedEditPermission;

  const canEditAttendance = useMemo(() => {
    if (canManageWorkersAttendance) return true;
    if (hasDelegatedEditPermission) return true;
    if (currentWorkerId != null && workerId === currentWorkerId) return true;
    return false;
  }, [
    canManageWorkersAttendance,
    currentWorkerId,
    hasDelegatedEditPermission,
    workerId,
  ]);

  const shouldRedirectUnauthorizedEdit = useMemo(
    () => isDelegatedPermissionsReady && wantsEditMode && !canEditAttendance,
    [canEditAttendance, isDelegatedPermissionsReady, wantsEditMode],
  );

  const canFetchByDate = useMemo(() => {
    if (!auth) return false;
    if (canManageWorkersAttendance) return true;
    if (!isDelegatedPermissionsReady) return false;
    return canViewWorker;
  }, [
    auth,
    canManageWorkersAttendance,
    canViewWorker,
    isDelegatedPermissionsReady,
  ]);

  // Delegated users: redirect only after logtime permissions finish loading.
  useEffect(() => {
    if (!shouldRedirectUnauthorizedViewer && !shouldRedirectUnauthorizedEdit) {
      return;
    }
    navigate(`/${ROUTES.NOT_FOUND_PAGE}`, { replace: true });
  }, [
    navigate,
    shouldRedirectUnauthorizedEdit,
    shouldRedirectUnauthorizedViewer,
  ]);

  const chooseDate = useMemo(() => searchParams.get("date"), [searchParams]);

  const [currentDate, setCurrentDate] = useState(() =>
    normalizeWorkDate(chooseDate),
  );

  const isPastDate = useMemo(() => isPastWorkDate(currentDate), [currentDate]);
  const isEditMode = useMemo(
    () => canEditAttendance && wantsEditMode && isPastDate,
    [canEditAttendance, isPastDate, wantsEditMode],
  );

  const [form] = Form.useForm();
  const watchedFormValues = Form.useWatch([], form) as
    | Record<string, unknown>
    | undefined;

  const { data, isLoading, refetch } = useFetchWorkerLogTimesByDate(
    currentDate,
    workerId,
    { enabled: canFetchByDate },
  );

  const viewingOwnToday =
    workerId != null &&
    currentWorkerId != null &&
    workerId === currentWorkerId &&
    isTodayWorkDate(currentDate);

  const { data: statusResponse } = useFetchWorkerLogTimes();

  const statusTodayLogs = useMemo(() => {
    if (!viewingOwnToday) return [];
    const workDate = toWorkDateApi(currentDate);
    return (statusResponse?.data?.today_logs ?? []).filter((log) => {
      const ymd = log.work_date?.trim().replace(/\//g, "-") ?? "";
      return !workDate || ymd === workDate;
    });
  }, [currentDate, statusResponse?.data?.today_logs, viewingOwnToday]);

  const [liveSessionOverride, setLiveSessionOverride] = useState<
    boolean | null
  >(null);
  const [closedAtMs, setClosedAtMs] = useState<number>();

  useEffect(() => {
    if (!workerId || !isTodayWorkDate(currentDate)) return;
    const workDate = toWorkDateApi(currentDate);

    return subscribeWorkerLogtimePunch((event) => {
      if (event.companyId !== companyId) return;
      if (event.workerId !== workerId) return;
      if (event.workDate && event.workDate !== workDate) return;

      const sessionOpen = isOpenSessionAfterPunch(event.logType);
      setLiveSessionOverride(sessionOpen);
      setClosedAtMs(sessionOpen ? undefined : Date.now());
      void refetch();
    });
  }, [companyId, currentDate, refetch, workerId]);

  useEffect(() => {
    if (liveSessionOverride === null || !data) return;

    const byDateOpen = hasOpenWorkSession(data.logtimes ?? []);
    const statusOpen =
      !statusTodayLogs.length || hasOpenWorkSession(statusTodayLogs);
    const syncedOpen = byDateOpen && statusOpen;

    if (syncedOpen === liveSessionOverride) {
      setLiveSessionOverride(null);
      setClosedAtMs(undefined);
    }
  }, [data, liveSessionOverride, statusTodayLogs]);

  const isLiveSessionOpen = useMemo(() => {
    if (!isTodayWorkDate(currentDate)) return false;
    if (liveSessionOverride !== null) return liveSessionOverride;
    if (!data) return false;

    const byDateOpen = hasOpenWorkSession(data.logtimes ?? []);
    if (!byDateOpen) return false;

    if (statusTodayLogs.length > 0 && !hasOpenWorkSession(statusTodayLogs)) {
      return false;
    }

    return true;
  }, [currentDate, data, liveSessionOverride, statusTodayLogs]);

  const hasFormChanges = useMemo(() => {
    if (!isEditMode || !data) return false;
    return hasWorkerLogtimesFormChanges(
      data,
      watchedFormValues ?? {},
      canEditAllAttendanceFields,
    );
  }, [isEditMode, data, watchedFormValues, canEditAllAttendanceFields]);

  const saveMutation = useSaveWorkerLogtimesSave();
  const submitInFlightRef = useRef(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const shouldPollWorkerLogtimesByDate = useMemo(() => {
    if (!data || isEditMode || isSubmitting || saveMutation.isLoading)
      return false;
    if (!isTodayWorkDate(currentDate)) return false;
    return isLiveSessionOpen;
  }, [
    currentDate,
    data,
    isEditMode,
    isLiveSessionOpen,
    isSubmitting,
    saveMutation.isLoading,
  ]);

  const shouldLiveSummary = shouldPollWorkerLogtimesByDate;

  const summaryServerNowMs = useServerNowMs(shouldLiveSummary);

  const liveSummaryLogtimes = useMemo(() => {
    if (!shouldLiveSummary || !viewingOwnToday) return undefined;
    return statusTodayLogs.length > 0 ? statusTodayLogs : undefined;
  }, [shouldLiveSummary, statusTodayLogs, viewingOwnToday]);

  useEffect(() => {
    if (!isTodayWorkDate(currentDate) || isEditMode) return;

    const refetchIfVisible = () => {
      if (document.visibilityState === "visible") {
        void refetch();
      }
    };

    window.addEventListener("focus", refetchIfVisible);
    document.addEventListener("visibilitychange", refetchIfVisible);

    return () => {
      window.removeEventListener("focus", refetchIfVisible);
      document.removeEventListener("visibilitychange", refetchIfVisible);
    };
  }, [currentDate, isEditMode, refetch]);

  useEffect(() => {
    if (!shouldPollWorkerLogtimesByDate) return;

    let intervalId: ReturnType<typeof setInterval> | undefined;
    const timeoutId = window.setTimeout(() => {
      void refetch();
      intervalId = setInterval(() => {
        void refetch();
      }, 60_000);
    }, msUntilNextMinuteTokyo());

    return () => {
      window.clearTimeout(timeoutId);
      if (intervalId !== undefined) clearInterval(intervalId);
    };
  }, [shouldPollWorkerLogtimesByDate, refetch]);

  useEffect(() => {
    const nextDate = normalizeWorkDate(chooseDate);
    if (nextDate !== currentDate) {
      setCurrentDate(nextDate);
    }
  }, [chooseDate, currentDate]);

  useEffect(() => {
    if (canManageWorkersAttendance || currentWorkerId == null) return;
    const wid = parseWorkerId(searchParams.get("worker_id"));
    if (wid != null) return;
    const next = new URLSearchParams(searchParams);
    next.set("worker_id", String(currentWorkerId));
    next.delete("mode");
    setSearchParams(next, { replace: true });
  }, [
    canManageWorkersAttendance,
    currentWorkerId,
    searchParams,
    setSearchParams,
  ]);

  const handleOnChangeDate = useCallback(
    (dateString: string) => {
      const workDate = toWorkDateApi(dateString);
      const next = new URLSearchParams(searchParams);
      next.set("date", workDate);
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const exitEditMode = useCallback(() => {
    if (searchParams.get("mode")) {
      const next = new URLSearchParams(searchParams);
      next.delete("mode");
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const handleSubmit = useCallback(
    async (values: Record<string, unknown>) => {
      if (submitInFlightRef.current) return;
      submitInFlightRef.current = true;
      setIsSubmitting(true);

      try {
        if (!data) {
          notification.error({ message: "データがありません。" });
          return;
        }
        const companyId = getCompanyId();
        if (!companyId) {
          notification.error({ message: "会社が選択されていません。" });
          return;
        }
        const wid = data.worker_id;
        if (!wid) {
          notification.error({ message: "worker_id が不正です。" });
          return;
        }

        if (!canEditAttendance) {
          notification.error({ message: "編集権限がありません。" });
          return;
        }

        const workDate = toWorkDateApi(currentDate);
        const submittedValues = {
          ...form.getFieldsValue(true),
          ...values,
        };

        if (
          !hasWorkerLogtimesFormChanges(
            data,
            submittedValues,
            canEditAllAttendanceFields,
          )
        ) {
          return;
        }

        let dataForSave = data;
        let valuesForSave = submittedValues;
        let idMap = new Map<number, number>();
        let savedAnything = false;

        if (canEditAttendance) {
          try {
            if (canEditAllAttendanceFields) {
              validateTrackingTimelineForm(data, submittedValues);
              await validateCrossCompanyOverlapBeforeSave({
                companyId,
                workerId: wid,
                workDate,
                data,
                formValues: submittedValues,
              });

              const jsonBuilt = buildWorkerLogtimesSaveJsonPayload(
                companyId,
                wid,
                workDate,
                data,
                submittedValues,
              );
              if (jsonBuilt.hasJsonPayload) {
                await saveMutation.mutateAsync(jsonBuilt.payload);
                savedAnything = true;
              }

              if (jsonBuilt.hasJsonPayload) {
                const jsonRefetch = await refetch();
                const dataAfterJson = jsonRefetch.data ?? data;
                valuesForSave = remapFormKeysAfterJsonSave(
                  data,
                  dataAfterJson,
                  submittedValues,
                );
                dataForSave = dataAfterJson;
              }

              idMap = await createVirtualLogtimesBeforeSave({
                data: dataForSave,
                formValues: valuesForSave,
                workDate,
                workerId: wid,
                companyId,
                canEditAllAttendanceFields,
              });
              if (idMap.size > 0) {
                const refetchResult = await refetch();
                dataForSave = refetchResult.data ?? dataForSave;
                valuesForSave = remapFormKeysAfterCreate(valuesForSave, idMap);
                valuesForSave = syncFormAttachmentsAfterWorkerLogtimeCreate(
                  dataForSave,
                  valuesForSave,
                  idMap,
                );
              }

              const attachmentBuilt = buildWorkerLogtimesSaveFormData(
                companyId,
                wid,
                workDate,
                dataForSave,
                valuesForSave,
                canEditAllAttendanceFields,
                { attachmentsOnly: true },
              );
              if (!attachmentBuilt.hasLogtimePayload && !savedAnything) {
                return;
              }
              if (attachmentBuilt.hasLogtimePayload) {
                await saveMutation.mutateAsync(attachmentBuilt.formData);
                savedAnything = true;
                const attachmentRefetch = await refetch();
                dataForSave = attachmentRefetch.data ?? dataForSave;
              }

              if (savedAnything) {
                form.setFieldsValue(valuesForSave);
                notification.success({ message: "保存しました。" });
                exitEditMode();
              }
              return;
            }

            idMap = await createVirtualLogtimesBeforeSave({
              data: dataForSave,
              formValues: submittedValues,
              workDate,
              workerId: wid,
              companyId,
              canEditAllAttendanceFields,
            });
            if (idMap.size > 0) {
              const refetchResult = await refetch();
              dataForSave = refetchResult.data ?? dataForSave;
              valuesForSave = remapFormKeysAfterCreate(submittedValues, idMap);
              valuesForSave = syncFormAttachmentsAfterWorkerLogtimeCreate(
                dataForSave,
                valuesForSave,
                idMap,
              );
              form.setFieldsValue(valuesForSave);
            }
          } catch (createErr: unknown) {
            if (createErr instanceof TimelineValidationError) {
              createErr.messages.forEach((msg) => {
                notification.error({ message: msg });
              });
              return;
            }
            if (createErr instanceof CrossCompanyOverlapError) {
              notification.error({ message: createErr.message });
              return;
            }
            notification.error({
              message: getWorkerLogtimeApiErrorMessage(
                createErr,
                canEditAllAttendanceFields
                  ? "保存に失敗しました。"
                  : "打刻の作成に失敗しました。",
              ),
            });
            return;
          }
        }

        const built = buildWorkerLogtimesSaveFormData(
          companyId,
          wid,
          workDate,
          dataForSave,
          valuesForSave,
          canEditAllAttendanceFields,
        );

        try {
          if (!built.hasLogtimePayload && idMap.size === 0) {
            return;
          }
          if (built.hasLogtimePayload) {
            await saveMutation.mutateAsync(built.formData);
            await refetch();
          }
          notification.success({ message: "保存しました。" });
          exitEditMode();
        } catch (err: unknown) {
          notification.error({
            message: getWorkerLogtimeApiErrorMessage(
              err,
              "保存に失敗しました。",
            ),
          });
        }
      } finally {
        submitInFlightRef.current = false;
        setIsSubmitting(false);
      }
    },
    [
      canEditAttendance,
      canEditAllAttendanceFields,
      currentDate,
      data,
      exitEditMode,
      form,
      refetch,
      saveMutation,
    ],
  );

  const handleBackToList = useCallback(() => {
    navigate(listPath);
  }, [navigate, listPath]);

  const onManagementCompanyChange = useCallback(
    (nextCompanyId: string) => {
      setCompanyId(nextCompanyId);
      const next = new URLSearchParams(searchParams);
      next.delete("worker_id");
      next.delete("mode");
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const onManagementWorkerChange = useCallback(
    (wid: number | null) => {
      const next = new URLSearchParams(searchParams);
      if (wid == null || !Number.isFinite(wid)) {
        next.delete("worker_id");
      } else {
        next.set("worker_id", String(wid));
      }
      next.delete("mode");
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  return {
    ...props,
    form,
    currentDate,
    isEditMode,
    handleOnChangeDate,
    data,
    handleSubmit,
    hasFormChanges,
    isPageLoading:
      (isLoading && !data) ||
      (shouldFetchLogtimePermissions && !isDelegatedPermissionsReady),
    isSaving: isSubmitting || saveMutation.isLoading,
    handleBackToList,
    exitEditMode,
    canEditAttendance,
    canEditAllAttendanceFields,
    canManageWorkersAttendance,
    isSuperAdmin,
    companies,
    companyId: companyId ?? "",
    selectedWorkerId: workerId,
    onManagementCompanyChange,
    onManagementWorkerChange,
    summaryServerNowMs,
    liveSummaryLogtimes,
    isLiveSessionOpen,
    summaryClosedAtMs: closedAtMs,
  };
};

export type Props = ReturnType<typeof useDetailTracking>;

export default useDetailTracking;
